import * as React from "react"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/class-control"
import { Button } from "@/components/ui/button"

export type CalendarProps = React.ComponentProps<"div"> & {
  mode?: "single" | "multiple" | "range"
  selected?: Date | Date[] | { from: Date; to?: Date }
  onSelect?: (date: Date | Date[] | { from: Date; to?: Date } | undefined) => void
  disabledPeriod?: (date: Date) => boolean
  // locale?: Locale
  showOutsideDays?: boolean
  disabled?: boolean
}

function Calendar({
  className,
  mode = "single",
  selected,
  onSelect,
  disabledPeriod,
  ...props
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(new Date())
  const [showYearPicker, setShowYearPicker] = React.useState(false)
  const [showMonthPicker, setShowMonthPicker] = React.useState(false)

  const monthNames = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ]

  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]

  const currentYear = currentMonth.getFullYear()
  const startYear = currentYear - 50
  const endYear = currentYear + 10
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const isSelected = (date: Date) => {
    if (!selected) return false
    if (mode === "single" && selected instanceof Date) {
      return date.toDateString() === selected.toDateString()
    }
    return false
  }

  const isDisabled = (date: Date) => {
    return disabledPeriod ? disabledPeriod(date) : false
  }

  const handleDateClick = (date: Date) => {
    if (isDisabled(date)) return
    if (onSelect) {
      onSelect(date)
    }
  }

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev)
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const handleYearSelect = (year: number) => {
    setCurrentMonth(prev => new Date(year, prev.getMonth(), 1))
    setShowYearPicker(false)
    setShowMonthPicker(false)
  }

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), monthIndex, 1))
    setShowMonthPicker(false)
  }

  const toggleYearPicker = () => {
    setShowYearPicker(!showYearPicker)
    setShowMonthPicker(false)
  }

  const toggleMonthPicker = () => {
    setShowMonthPicker(!showMonthPicker)
    setShowYearPicker(false)
  }

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const days = []

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-9 w-9" />
      )
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
      const selected = isSelected(date)
      const disabled = isDisabled(date)

      days.push(
        <button
          key={day}
          type="button"
          className={cn(
            "h-9 w-9 p-0 font-normal text-sm rounded-md transition-colors",
            "hover:bg-gray-700 focus:bg-gray-700 focus:outline-none",
            selected && "bg-primary text-white hover:bg-primary/90",
            disabled && "text-gray-500 cursor-not-allowed hover:bg-transparent",
            !selected && !disabled && "text-white"
          )}
          disabled={disabled}
          onClick={() => handleDateClick(date)}
        >
          {day}
        </button>
      )
    }

    return days
  }

  const renderYearPicker = () => {
    return (
      <div className="grid grid-cols-4 gap-2 p-2 max-h-48 overflow-y-auto">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            className={cn(
              "h-8 px-2 text-sm rounded-md transition-colors",
              "hover:bg-gray-700 focus:bg-gray-700 focus:outline-none",
              year === currentYear && "bg-primary text-white hover:bg-primary/90",
              year !== currentYear && "text-white"
            )}
            onClick={() => handleYearSelect(year)}
          >
            {year}
          </button>
        ))}
      </div>
    )
  }

  const renderMonthPicker = () => {
    return (
      <div className="grid grid-cols-3 gap-2 p-2">
        {monthNames.map((month, index) => (
          <button
            key={month}
            type="button"
            className={cn(
              "h-8 px-2 text-sm rounded-md transition-colors",
              "hover:bg-gray-700 focus:bg-gray-700 focus:outline-none",
              index === currentMonth.getMonth() && "bg-primary text-white hover:bg-primary/90",
              index !== currentMonth.getMonth() && "text-white"
            )}
            onClick={() => handleMonthSelect(index)}
          >
            {month.substring(0, 3)}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className={cn("p-3 bg-gray-800 border border-gray-600 rounded-md", className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-white hover:bg-gray-700"
          onClick={() => navigateMonth("prev")}
          disabled={showYearPicker || showMonthPicker}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="text-sm font-medium text-white hover:bg-gray-700 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
            onClick={toggleMonthPicker}
          >
            {monthNames[currentMonth.getMonth()]}
            <ChevronDown className="h-3 w-3" />
          </button>
          
          <button
            type="button"
            className="text-sm font-medium text-white hover:bg-gray-700 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
            onClick={toggleYearPicker}
          >
            {currentMonth.getFullYear()}
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-white hover:bg-gray-700"
          onClick={() => navigateMonth("next")}
          disabled={showYearPicker || showMonthPicker}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Year Picker */}
      {showYearPicker && (
        <div className="mb-4">
          <div className="text-xs text-gray-400 mb-2 text-center">Selecione o ano</div>
          {renderYearPicker()}
        </div>
      )}

      {/* Month Picker */}
      {showMonthPicker && (
        <div className="mb-4">
          <div className="text-xs text-gray-400 mb-2 text-center">Selecione o mês</div>
          {renderMonthPicker()}
        </div>
      )}

      {/* Calendar View */}
      {!showYearPicker && !showMonthPicker && (
        <>
          {/* Day names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day) => (
              <div key={day} className="h-9 w-9 flex items-center justify-center text-xs font-medium text-gray-400">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendarDays()}
          </div>
        </>
      )}
    </div>
  )
}

Calendar.displayName = "Calendar"

export { Calendar }